// src/pages/Dashboard/sections/InvitationManagement.jsx
import React, { useState, useEffect } from 'react';
import * as nodeManagementApi from '../../../services/nodeManagementApi';
import { generateSignatureMessage, signMessage } from '../../../services/api';
import { generateInvitationLink, shareInvitationLink, copyToClipboard } from '../../../utils/invitationUtils';
import LoadingSpinner from '../../../components/NodeManagement/components/LoadingSpinner';
import ErrorMessage from '../../../components/NodeManagement/components/ErrorMessage';
import SuccessMessage from '../../../components/NodeManagement/components/SuccessMessage';
import '../styles/invitationManagement.css';

/**
 * Invitation Management Section
 * Handles node-specific invitations and referral tracking
 * Updated to match API documentation for node invitations
 */
const InvitationManagement = ({ 
  wallet, 
  activatedNodes, 
  selectedNode, 
  onNodeChange, 
  formatDate 
}) => {
  const [activeTab, setActiveTab] = useState('invitations');
  const [invitationTab, setInvitationTab] = useState('code'); // 'code' or 'link'
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copyText, setCopyText] = useState('Copy');
  const [linkCopyText, setLinkCopyText] = useState('Copy Link');
  const [nodeInvitationCodes, setNodeInvitationCodes] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedReferralPeriod, setSelectedReferralPeriod] = useState('all'); // 'all', '30d', '7d'
  
  // Fetch invitation codes for each node when component mounts
  useEffect(() => {
    if (selectedNode) {
      fetchNodeInvitationCode(selectedNode.id);
      
      if (activeTab === 'referrals') {
        fetchReferrals();
      }
    }
  }, [selectedNode, activeTab, selectedReferralPeriod]);
  
  // Fetch invitation code for a specific node
  const fetchNodeInvitationCode = async (nodeId) => {
    if (!wallet.connected || !nodeId) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      // Check if we already have this node's invitation code cached
      if (nodeInvitationCodes[nodeId]) {
        setIsLoading(false);
        return;
      }
      
      // Get signature message for authentication
      const messageResponse = await generateSignatureMessage(
        wallet.address,
        wallet.networkId || 1
      );
      
      if (!messageResponse || !messageResponse.message) {
        throw new Error('Invalid signature message response');
      }
      
      // Sign message with wallet
      const signature = await signMessage(
        wallet.type,
        wallet.address,
        messageResponse.message
      );
      
      // Use the generate-for-node endpoint as per API documentation
      const response = await fetch('https://api.aeronyx.network/api/invitation-code/generate-for-node/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_id: nodeId,
          wallet_address: wallet.address,
          blockchain_network_id: wallet.networkId || 1,
          signature: signature,
          message: messageResponse.message,
          wallet_type: wallet.type
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Cache the invitation code
        setNodeInvitationCodes(prev => ({
          ...prev,
          [nodeId]: {
            code: result.data.invitation_code,
            isNodeSpecific: true,
            totalReferrals: result.data.total_referrals || 0,
            nodeReferenceCode: result.data.node_reference_code
          }
        }));
      } else {
        console.error('Failed to get node invitation code:', result.message);
        setError(result.message || 'Failed to load invitation code');
      }
    } catch (err) {
      console.error('Error fetching node invitation code:', err);
      setError('Failed to load invitation code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch referrals for the selected node
  const fetchReferrals = async () => {
    if (!wallet.connected || !selectedNode) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      // Prepare time period filter
      let period = null;
      if (selectedReferralPeriod === '7d') {
        period = {
          start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: new Date().toISOString()
        };
      } else if (selectedReferralPeriod === '30d') {
        period = {
          start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: new Date().toISOString()
        };
      }
      
      // Get signature message for authentication
      const messageResponse = await generateSignatureMessage(
        wallet.address,
        wallet.networkId || 1
      );
      
      if (!messageResponse || !messageResponse.message) {
        throw new Error('Invalid signature message response');
      }
      
      // Sign message with wallet
      const signature = await signMessage(
        wallet.type,
        wallet.address,
        messageResponse.message
      );
      
      // API call to fetch referrals
      const response = await fetch('https://api.aeronyx.network/api/nodes/referrals/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: wallet.address,
          blockchain_network_id: wallet.networkId || 1,
          signature: signature,
          message: messageResponse.message,
          wallet_type: wallet.type,
          node_id: selectedNode.id,
          page: currentPage,
          page_size: pageSize,
          period: period
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setReferrals(result.data.referrals || []);
        setTotalReferrals(result.data.total_count || 0);
        setTotalPages(Math.ceil((result.data.total_count || 0) / pageSize));
      } else {
        console.error('Failed to fetch referrals:', result.message);
        setError(result.message || 'Failed to load referrals');
      }
    } catch (err) {
      console.error('Error fetching referrals:', err);
      setError('Failed to load referrals. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get the current node's invitation code
  const getCurrentNodeInvitationCode = () => {
    if (!selectedNode) return '';
    return nodeInvitationCodes[selectedNode.id]?.code || '';
  };
  
  // Get invitation link for the current code
  const getInvitationLink = () => {
    const code = getCurrentNodeInvitationCode();
    return code ? generateInvitationLink(code) : '';
  };
  
  // Handle copy code button click
  const handleCopyCode = () => {
    const code = getCurrentNodeInvitationCode();
    if (!code) return;
    
    copyToClipboard(
      code,
      (msg) => {
        setCopyText('Copied!');
        setSuccess(msg);
        setTimeout(() => {
          setCopyText('Copy');
          setSuccess('');
        }, 2000);
      },
      (errMsg) => {
        setError(errMsg);
      }
    );
  };
  
  // Handle copy link button click
  const handleCopyLink = () => {
    const link = getInvitationLink();
    if (!link) return;
    
    copyToClipboard(
      link,
      (msg) => {
        setLinkCopyText('Copied!');
        setSuccess(msg);
        setTimeout(() => {
          setLinkCopyText('Copy Link');
          setSuccess('');
        }, 2000);
      },
      (errMsg) => {
        setError(errMsg);
      }
    );
  };
  
  // Handle share link button click
  const handleShareLink = () => {
    const code = getCurrentNodeInvitationCode();
    if (!code) return;
    
    shareInvitationLink(
      code,
      (msg) => {
        setSuccess(msg);
        setTimeout(() => {
          setSuccess('');
        }, 2000);
      },
      (errMsg) => {
        setError(errMsg);
      }
    );
  };
  
  // Create social share URL
  const createShareUrl = (platform, code) => {
    if (!code) return '#';
    
    const text = `Join my node team on Aeronyx Network! Use my node invitation code ${code} to get started.`;
    const url = generateInvitationLink(code);
    
    if (platform === 'twitter') {
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    } else if (platform === 'telegram') {
      return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    }
    return '#';
  };
  
  // Handle page change in referrals pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    
    setCurrentPage(newPage);
  };
  
  // Format referral date
  const formatReferralDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    return formatDate 
      ? formatDate(dateString) 
      : new Date(dateString).toLocaleDateString();
  };
  
  // Get tier badge class
  const getTierBadgeClass = (tier) => {
    if (!tier) return 'tier-standard';
    
    const lowerTier = tier.toLowerCase();
    if (lowerTier.includes('apex')) return 'tier-apex';
    if (lowerTier.includes('master')) return 'tier-master';
    if (lowerTier.includes('super')) return 'tier-super';
    return 'tier-standard';
  };
  
  const currentInvitationCode = getCurrentNodeInvitationCode();
  const currentInvitationLink = getInvitationLink();
  
  if (!wallet.connected) {
    return (
      <div className="section-placeholder">
        <p>Please connect your wallet to manage your invitations.</p>
      </div>
    );
  }
  
  return (
    <div className="invitation-management-section">
      <div className="section-header">
        <h2>Invitation Management</h2>
        <p className="section-description">
          Generate and share node-specific invitation codes to grow your team network.
        </p>
      </div>
      
      {selectedNode ? (
        <>
          <div className="tab-navigation">
            <button
              className={`tab-btn ${activeTab === 'invitations' ? 'active' : ''}`}
              onClick={() => setActiveTab('invitations')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 16H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Invitation Codes</span>
            </button>
            
            <button
              className={`tab-btn ${activeTab === 'referrals' ? 'active' : ''}`}
              onClick={() => setActiveTab('referrals')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Referrals</span>
              <span className="counter">
                {nodeInvitationCodes[selectedNode.id]?.totalReferrals || 0}
              </span>
            </button>
          </div>
          
          {activeTab === 'invitations' ? (
            <div className="invitations-content">
              <div className="selected-node-info">
                <div className="node-reference">
                  <span className="label">Node:</span>
                  <span className="code">{selectedNode.reference_code}</span>
                </div>
                <div className="node-tier">
                  <span className="label">Tier:</span>
                  <span className={`value ${getTierBadgeClass(selectedNode.tier || selectedNode.tier_name)}`}>
                    {selectedNode.tier_name || selectedNode.tier || 'Standard'}
                  </span>
                </div>
                <div className="node-referrals">
                  <span className="label">Referrals:</span>
                  <span className="value">{nodeInvitationCodes[selectedNode.id]?.totalReferrals || 0}</span>
                </div>
              </div>
              
              <div className="invitation-tabs">
                <button 
                  className={`invitation-tab-btn ${invitationTab === 'code' ? 'active' : ''}`}
                  onClick={() => setInvitationTab('code')}
                >
                  Invitation Code
                </button>
                <button 
                  className={`invitation-tab-btn ${invitationTab === 'link' ? 'active' : ''}`}
                  onClick={() => setInvitationTab('link')}
                >
                  Invitation Link
                </button>
              </div>
              
              <p className="invitation-description">
                Share this node-specific invitation code for others to join your team directly under this node.
                Using this code will directly connect them to this node in your network.
              </p>
              
              {isLoading ? (
                <div className="loading-container">
                  <LoadingSpinner />
                  <p>Loading invitation code...</p>
                </div>
              ) : error ? (
                <ErrorMessage message={error} />
              ) : invitationTab === 'code' ? (
                <div className="invitation-code-display">
                  <div className="code-type node-specific-code">NODE CODE</div>
                  <div className="code" aria-label="Your node invitation code">{currentInvitationCode}</div>
                  <button 
                    className="copy-btn" 
                    onClick={handleCopyCode}
                    aria-label="Copy invitation code"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.188 2.602C16.0017 2.4115 15.7793 2.25992 15.5338 2.15654C15.2882 2.05316 15.0244 1.99989 14.758 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="invitation-link-container">
                  <div className="invitation-link">{currentInvitationLink}</div>
                  <div className="link-actions">
                    <button 
                      className="btn btn-outline btn-sm" 
                      onClick={handleCopyLink}
                    >
                      {linkCopyText}
                    </button>
                    <button 
                      className="btn btn-primary btn-sm" 
                      onClick={handleShareLink}
                    >
                      Share
                    </button>
                  </div>
                </div>
              )}
              
              {success && (
                <div className="copy-success">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{success}</span>
                </div>
              )}
              
              {/* Share Options */}
              {currentInvitationCode && (
                <div className="share-options">
                  <div className="share-heading">Share via</div>
                  <div className="share-links">
                    <a 
                      href={createShareUrl('twitter', currentInvitationCode)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="share-link twitter"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 3.01s-2.018 1.192-3.14 1.53a4.48 4.48 0 00-7.86 3v1a10.66 10.66 0 01-9-4.53s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5 0-.278-.028-.556-.08-.83C21.94 5.674 23 3.01 23 3.01z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Twitter
                    </a>
                    
                    <a 
                      href={createShareUrl('telegram', currentInvitationCode)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="share-link telegram"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.05 2.31c-.439-.388-1.09-.495-1.93-.326-1.013.202-17.194 6.733-18.344 7.472-.48.34-.773.601-.802.941-.04.77.513 1.094.513 1.094l4.36 1.429c.306.656 1.48 5.715 1.71 6.432.15.488.288.707.428.8.255.17.51.134.51.134.345-.17 1.984-1.921 2.517-2.436 1.651 1.109 3.223 2.201 3.836 2.74.427.373.79.579 1.123.688.433.149.83.176 1.164.106.467-.099.985-.483 1.113-1.83.134-1.387 1.446-15.704 1.472-16.496.043-.937-.293-1.342-.67-1.748zm-3.207 2.769L7.734 11.602l-.56.193.076-.561 10.816-6.57c.351-.118.86-.322.86.071-.001.297-.083.334-.083.334z"/>
                      </svg>
                      Telegram
                    </a>

                    <a 
                      href={`mailto:?subject=Join%20my%20Aeronyx%20Network%20team&body=Join%20my%20node%20team%20on%20Aeronyx%20Network!%20Use%20my%20node%20invitation%20code%20${currentInvitationCode}%20to%20get%20started.%0A%0A${encodeURIComponent(currentInvitationLink)}`} 
                      className="share-link email"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 6l-10 7L2 6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Email
                    </a>
                  </div>
                </div>
              )}
              
              <div className="invitation-instructions">
                <h3>How to use your invitation code</h3>
                <ol className="instructions-list">
                  <li>Share your node-specific invitation code with potential team members</li>
                  <li>Ask them to enter the code during their node application process</li>
                  <li>Once approved, they'll be connected directly to your node</li>
                  <li>Track your referrals in the "Referrals" tab</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="referrals-content">
              <div className="referrals-header">
                <h3>Referrals for Node {selectedNode.reference_code}</h3>
                <div className="period-filter">
                  <label>Time Period:</label>
                  <select 
                    value={selectedReferralPeriod}
                    onChange={(e) => {
                      setSelectedReferralPeriod(e.target.value);
                      setCurrentPage(1); // Reset to first page when changing filter
                    }}
                  >
                    <option value="all">All Time</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="7d">Last 7 Days</option>
                  </select>
                </div>
              </div>
              
              {isLoading ? (
                <div className="loading-container">
                  <LoadingSpinner />
                  <p>Loading referrals...</p>
                </div>
              ) : error ? (
                <ErrorMessage message={error} />
              ) : referrals.length > 0 ? (
                <>
                  <div className="referrals-table-container">
                    <table className="referrals-table">
                      <thead>
                        <tr>
                          <th>Node</th>
                          <th>Tier</th>
                          <th>Status</th>
                          <th>Joined Date</th>
                          <th>Network Stability Fees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((referral, index) => (
                          <tr key={index}>
                            <td className="node-col">
                              <span className="referral-node-code">{referral.reference_code || 'N/A'}</span>
                            </td>
                            <td>
                              <span className={`tier-badge ${getTierBadgeClass(referral.tier)}`}>
                                {referral.tier || 'Standard'}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${referral.status || 'pending'}`}>
                                {referral.status || 'Pending'}
                              </span>
                            </td>
                            <td className="date-col">{formatReferralDate(referral.created_at)}</td>
                            <td className="commission-col">
                              {referral.network_stability_fees ? 
                                Number(referral.network_stability_fees).toLocaleString('en-US', { maximumFractionDigits: 2 }) : 
                                (referral.commissions ? 
                                  Number(referral.commissions).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '0')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-referrals">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M23 21V19C22.9986 17.1771 21.765 15.5857 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 3.13C17.7699 3.58317 19.0078 5.17799 19.0078 7.005C19.0078 8.83201 17.7699 10.4268 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 4L21 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>No Referrals Found</h3>
                  <p>You don't have any referrals for this node yet. Share your invitation code to start building your team.</p>
                  <button className="btn btn-primary" onClick={() => setActiveTab('invitations')}>
                    Generate Invitation Code
                  </button>
                </div>
              )}
              
              <div className="referral-stats">
                <div className="stat-card">
                  <div className="stat-value">{totalReferrals}</div>
                  <div className="stat-label">Total Referrals</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">
                    {referrals.filter(r => r.status === 'active').length}
                  </div>
                  <div className="stat-label">Active Referrals</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">
                    {referrals.reduce((sum, r) => {
                      // Check for network_stability_fees first, fall back to commissions
                      const value = r.network_stability_fees !== undefined ? 
                        Number(r.network_stability_fees) : 
                        (Number(r.commissions) || 0);
                      return sum + value;
                    }, 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </div>
                  <div className="stat-label">Total Network Stability Fees</div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="no-nodes-message">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="info-icon">
            <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>No Activated Nodes</h3>
          <p>You don't have any activated nodes yet. Apply for a node to generate invitation codes.</p>
          <a href="/node-application" className="btn btn-primary">Apply for a Node</a>
        </div>
      )}
      
      <div className="invitation-tips">
        <h3>Tips for Growing Your Network</h3>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 7L7 12V17H9L13 12L15 17L17 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4>Target Active Communities</h4>
            <p>Share your invitation code in blockchain and cryptocurrency communities where people are already interested in node operations.</p>
          </div>
          
          <div className="tip-card">
            <div className="tip-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor"/>
                <path d="M8.5 15C8.5 15 10 17 12 17C14 17 15.5 15 15.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4>Personalize Your Message</h4>
            <p>When sharing your invitation code, explain the benefits of joining your specific node and what kind of support you offer to team members.</p>
          </div>
          
          <div className="tip-card">
            <div className="tip-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4>Provide Clear Instructions</h4>
            <p>Ensure your referrals know exactly how to use your invitation code during their application process to avoid any connection issues.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationManagement;
